/**
 * Global Casino Providers tag mutations.
 *
 * The collection-scoped `useSetProviderTags(collectionCode)` in
 * `casino-configuration/collections/useCollectionProviders` invalidates the
 * collection's provider list — wrong cache key for the global Casino
 * Providers page. This module owns the global path: same BE endpoint,
 * different cache invalidation target.
 *
 * BE contract:
 *   POST   /admin-panel-casino/v3/providers/{provider_id}/tags    body: { tag_ids: string[] }
 *   DELETE /admin-panel-casino/v3/providers/{provider_id}/tags/{tag_id}
 *
 * Single-tag-per-provider rule is enforced by the modal (it sends the
 * REMOVE before the SET on a replace), not the BE — the API itself
 * accepts arrays so a future "multi-tag per provider" change is just a
 * UI flip.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";

export const CASINO_PROVIDERS_GLOBAL_QUERY_KEY = [
  "casinoConfiguration",
  "providers",
  "global",
] as const;

function getAuthedInstance() {
  const token = JSON.parse(localStorage.getItem("token") ?? "null");
  return createPrivateAdminPanelInstance(token?.AccessToken);
}

export type SetCasinoProviderTagsArgs = {
  providerId: string;
  tagIds: string[];
};

export type RemoveCasinoProviderTagArgs = {
  providerId: string;
  tagId: string;
};

export async function setCasinoProviderTags({
  providerId,
  tagIds,
}: SetCasinoProviderTagsArgs): Promise<unknown> {
  const instance = getAuthedInstance();
  const url = API_ROUTES.SET_PROVIDER_TAGS.replace(
    "{provider_id}",
    encodeURIComponent(providerId),
  );
  const { data } = await instance.post(url, { tag_ids: tagIds });
  if (data?.success === false) {
    throw new Error(data?.message || "Failed to set provider tag");
  }
  return data;
}

export async function removeCasinoProviderTag({
  providerId,
  tagId,
}: RemoveCasinoProviderTagArgs): Promise<void> {
  const instance = getAuthedInstance();
  const url = API_ROUTES.DELETE_PROVIDER_TAG
    .replace("{provider_id}", encodeURIComponent(providerId))
    .replace("{tag_id}", encodeURIComponent(tagId));
  const { data } = await instance.delete(url);
  if (data && typeof data === "object" && data.success === false) {
    throw new Error(data?.message || "Failed to remove provider tag");
  }
}

/**
 * Mutation for setting a provider's tag from the global Casino Providers
 * page. Invalidates the global provider list on success so the badge
 * reflects the new tag without a manual refresh.
 */
export function useSetCasinoProviderTags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setCasinoProviderTags,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: CASINO_PROVIDERS_GLOBAL_QUERY_KEY,
      });
    },
  });
}

/** Mutation for removing a provider's tag (used by the replace flow). */
export function useRemoveCasinoProviderTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeCasinoProviderTag,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: CASINO_PROVIDERS_GLOBAL_QUERY_KEY,
      });
    },
  });
}
